/**
 * 결재란 서명 표시 게이트 — DRAFT 서명 숨김(`shouldHideSignatures`) + 작성 완료 서명 게이트(F-25) +
 * 반려 뒤 재작성 판정(F-34).
 *
 * ## 왜 rc에 있는가 (2026-09-18 R6)
 * 종전엔 web `lib/authoringSignatureVisibility.ts`였고 admin은 앱 경계를 넘어 import할 수 없어
 * `lib/composerApprovalData.ts`가 「부분 미러」(DRAFT 게이트만)로 살았다 — F-25·F-34가 admin 상세에서
 * 갈리는 자리였다. 코어({@link buildApprovalBlockData})와 함께 rc로 올려 web·admin이 같은 술어를 쓴다.
 * web은 기존 경로(`lib/authoringSignatureVisibility`)를 re-export로 유지한다.
 *
 * ⚠️ web `composer/composerApprovalData`는 상세 spec 8개가 `vi.mock`한다 — 그 파일에 export를 더하면
 * 그 spec들이 `No "X" export is defined on the mock`으로 죽는다(vitest 팩토리 mock은 부분 mock이 아니다,
 * `memory/feedback-pure-predicate-must-not-live-in-a-vi-mocked-module`). 새 규칙·술어는 여기(rc)에.
 *
 * ## 계약 — 「DRAFT에는 서명이 없다」가 위임본에서는 참이 아니다
 * `shouldHideSignatures`의 근거(「DRAFT는 제출 전이라 임시 서명이 없을 수 있다」)는 **직접
 * 작성** 기준이다. 직접 작성은 상신하는 그 순간에 서명하므로 DRAFT 구간에 확정된 작성 서명이
 * 없다. 그러나 **위임본은 다르다** — 담당자가 외부 링크에서 「작성 완료」를 누른 시점에 서명이
 * 확정되고(`authorSignedAt`), 그 뒤로도 문서는 DRAFT에 머문다.
 *
 * 특히 TBM 양식은 스냅샷 `policy.postSubmit` 선언으로 작성 완료 직후의 결재 자동 시작을
 * 건너뛰므로(일반 COMPOSED는 `composed-external.service`가 즉시 시작한다), 배포 설정·교육이
 * 끝나 위임자가 상신할 때까지 **「DRAFT + 담당자 서명 확정 + 결재 서명 행 0건」**으로 며칠
 * 머문다. 그 구간 내내 담당 칸을 비워 두면 화면이 「담당자가 아직 서명하지 않았다」고
 * 거짓말한다. 이 조합은 postSubmit이 만든 새 조합이고, 그 전에는 존재한 적이 없다.
 *
 * 판정 축은 서버와 같다 — 담당 칸을 작성 서명으로 채우는 규칙의 정본은 shared
 * `isAuthoringSignatureStepDefinitionRow`이고, 어느 칸에 매핑하는지는 rc
 * `buildMergedApprovalItems`가 그 정본으로 정한다. 여기서는 **보일지 말지**만 정한다.
 */
import { latestRoundSignatures } from '@safetyhub/shared';

/**
 * DRAFT(제출 전) 상태에서만 결재 서명을 숨긴다.
 * REJECTED는 반려 표시(A안)를 위해 진행된 서명·반려 마크를 표시한다 — 보고서 상세 페이지와 일관.
 *
 * **DRAFT인가**만 묻는 축이다 — 작성 완료 서명(author*)의 표시는 이 술어가 아니라
 * {@link shouldHideAuthoringSignature}가 정한다(위임본은 DRAFT에도 확정된 작성 서명을 가진다).
 */
export function shouldHideSignatures(reportStatus: string | undefined): boolean {
  return reportStatus === 'DRAFT';
}

/**
 * 표시 게이트가 읽는 사실들 — 인증 상세(`Report`)·공유 응답(`ShareReportResponse`)·외부 결재
 * 응답(`ExternalSignatureReportView`)·admin 상세(`/admin/reports/:id`)가 모두 이 형상으로 좁혀진다.
 *
 * `delegatedContact`를 `unknown`으로 받는 이유: 계약마다 담당자 객체 형상이 서로 다르고
 * (인증 상세는 연락처까지, 공유·외부 결재·admin은 `{ id }`만) 우리는 **존재 여부**만 보기 때문이다. 구조를
 * 좁게 적으면 weak-type 검사에 걸려 호출부가 캐스트를 쓰게 된다.
 */
export interface AuthoringSignatureVisibilityInput {
  status?: string;
  /** 위임 사실의 정본(인증 상세). */
  delegatedContactId?: string | null;
  /** 위임 담당자 객체 — `delegatedContactId`가 없는 공개·admin 계약에서 위임 사실을 대신한다. */
  delegatedContact?: unknown;
  /** 담당자가 「작성 완료」를 누른 시각. 공개 계약에서는 `authorSignature.signedAt`. */
  authorSignedAt?: string | Date | null;
}

/**
 * 이 문서가 위임본인가 — 정본은 `delegatedContactId`, 그 필드가 없는 공개·admin 계약에서는 동봉된
 * 담당자 객체의 존재로 읽는다(서버가 위임본에만 싣는다).
 */
export function isDelegatedReport(input: AuthoringSignatureVisibilityInput): boolean {
  if (input.delegatedContactId) return true;
  return input.delegatedContact != null;
}

/**
 * 작성 완료 서명이 **확정**됐는가 = 위임본이고 담당자가 작성 완료를 눌렀다.
 *
 * 직접 작성은 상신 시점에 서명하므로 DRAFT 구간에 확정된 서명이 없다 — 그래서 위임 축을 함께
 * 요구한다. 위임 여부를 빼고 `authorSignedAt`만 보면, 직접 작성의 반려 후 재작성 구간처럼
 * 지난 라운드 값이 남아 있는 경우까지 「확정」으로 읽게 된다.
 */
export function hasConfirmedAuthoringSignature(
  input: AuthoringSignatureVisibilityInput,
): boolean {
  return isDelegatedReport(input) && input.authorSignedAt != null;
}

/**
 * 이 응답에서 작성 완료 서명(authorSignerName/authorSignatureData)을 가려야 하는가.
 *
 * `shouldHideSignatures`(DRAFT인가)에서 **확정된 작성 서명이 있는 경우만 빼낸다**. 결재 서명
 * 행(round 서명)의 숨김은 이 술어와 무관하다 — DRAFT에는 그 행이 아예 없다.
 */
export function shouldHideAuthoringSignature(
  input: AuthoringSignatureVisibilityInput,
): boolean {
  return shouldHideSignatures(input.status) && !hasConfirmedAuthoringSignature(input);
}

/** 반려 라운드 판정이 읽는 서명 행의 최소 형상 — 인증 상세 `Report['approvalSignatures']` 원소. */
export interface RejectedRoundSignatureInput {
  round?: number | null;
  status: string;
  signedAt?: string | Date | null;
}

function toMs(value: string | Date | null | undefined): number | null {
  if (value == null) return null;
  const ms = value instanceof Date ? value.getTime() : new Date(value).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/**
 * **반려 뒤 담당자가 다시 작성 완료했는가**(F-34, 2026-09-11 사용자 결정) — 이 구간의 결재란은
 * 「제출 전 모양」이다: 담당 칸 = 새 작성 서명, 나머지 칸은 빈칸. 반려된 라운드의 승인·반려는
 * 결재 이력에만 남는다.
 *
 * 왜 필요한가: 결재란은 «최신 round 서명»을 겹치고 숨김은 DRAFT만 본다. 그래서 반려(REJECTED)
 * 구간에는 round 1의 결과(1·2단계 승인 + 3단계 반려)가 그대로 그려지는데, 그건 **담당자가 다시
 * 쓰기 전**까지만 정직하다 — 재작성·재서명 뒤에도 옛 승인과 옛 서명이 남아 있으면 「이미 결재된
 * 문서」처럼 보인다(34-d 실측: B의 새 서명이 담당 칸에 안 실리고 round 1 승인이 그대로).
 *
 * 판정 축은 **시각 비교**다: `authorSignedAt`(새 작성 완료) > 최신 라운드의 반려 시각. 위임 여부는
 * 묻지 않는다 — 직접 작성은 반려 후 수정 시 서버가 `authorSignedAt`을 비우므로(`shouldResetSignatures`)
 * 이 술어가 참이 될 수 없고, 참이 되는 경우는 정확히 「담당자가 다시 작성 완료한 위임본」뿐이다.
 * 재상신하면 round가 +1 되어 최신 라운드에 반려가 없어지므로 자연히 거짓이 된다.
 */
export function isRewrittenAfterRejection(input: {
  status?: string;
  authorSignedAt?: string | Date | null;
  approvalSignatures?: ReadonlyArray<RejectedRoundSignatureInput> | null;
}): boolean {
  if (input.status !== 'REJECTED') return false;
  const signedMs = toMs(input.authorSignedAt);
  if (signedMs == null) return false;
  const sigs = input.approvalSignatures ?? [];
  if (sigs.length === 0) return false;
  let rejectedMs: number | null = null;
  // 최신 round 판정은 shared 정본(`latestRoundSignatures`)과 같은 축이다.
  for (const s of latestRoundSignatures(sigs)) {
    if (s.status !== 'REJECTED') continue;
    const ms = toMs(s.signedAt);
    if (ms != null && (rejectedMs == null || ms > rejectedMs)) rejectedMs = ms;
  }
  return rejectedMs != null && signedMs > rejectedMs;
}
